import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { Download, ArrowLeft } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_AIRCRAFT, DEMO_FLIGHTS, DEMO_UIDS } from '../../lib/demoData';
import { fmtHours, fmtUSD, rateFor, summarizeCurrentCycle } from '../../lib/billingCycles';
import { downloadStatementPdf } from '../../lib/pdf';
import type { AircraftDoc, FlightDoc } from '../../lib/types';
import { OWNER_EMAIL } from '../../lib/identity';

export function Statement() {
  const { cycleId } = useParams<{ cycleId: string }>(); // YYYY-MM-DD = cycle start
  const { profile, fbUser, demoMode } = useAuth();
  const nav = useNavigate();
  const [flights, setFlights] = useState<FlightDoc[]>([]);
  const [aircraft, setAircraft] = useState<AircraftDoc | null>(null);

  useEffect(() => {
    if (demoMode) {
      const uid = profile?.status === 'active' ? DEMO_UIDS.brian : profile?.uid ?? '';
      setFlights(DEMO_FLIGHTS.filter((f) => f.lesseeUid === uid));
      setAircraft(DEMO_AIRCRAFT[0]);
      return;
    }
    if (!db || !fbUser) return;
    const uf = onSnapshot(query(collection(db, 'flights'), where('lesseeUid', '==', fbUser.uid)), (s) =>
      setFlights(s.docs.map((d) => d.data() as FlightDoc))
    );
    const ua = onSnapshot(collection(db, 'aircraft'), (s) => setAircraft((s.docs[0]?.data() as AircraftDoc) ?? null));
    return () => { uf(); ua(); };
  }, [demoMode, fbUser, profile?.status, profile?.uid]);

  const cycle = useMemo(() => {
    if (!aircraft) return null;
    const ref = cycleId ? new Date(`${cycleId}T12:00:00`) : new Date();
    return summarizeCurrentCycle({ flights, aircraft, billingDayOfMonth: profile?.billingDayOfMonth ?? 1, ref , rate: rateFor(profile, aircraft) });
  }, [flights, aircraft, profile?.billingDayOfMonth, cycleId]);

  if (!aircraft || !cycle) {
    return <div className="card p-8 text-center text-ink-muted">Loading statement…</div>;
  }

  const pilotName =
    `${profile?.personal?.firstName ?? ''} ${profile?.personal?.lastName ?? ''}`.trim() || profile?.email || 'Pilot';
  const periodEnd = new Date(cycle.end.getTime() - 86400000);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <button onClick={() => nav('/billing')} className="btn-ghost inline-flex items-center gap-1 text-sm">
          <ArrowLeft size={14} /> Back to billing
        </button>
        <button
          onClick={() => downloadStatementPdf({ cycle, tailNumber: aircraft.tailNumber, pilotName })}
          className="btn-primary inline-flex items-center gap-1.5 text-sm"
        >
          <Download size={14} /> Download PDF
        </button>
      </div>

      <div className="card mx-auto max-w-3xl p-6 sm:p-10">
        <div className="flex items-start justify-between gap-4 border-b border-navy-900/15 pb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-muted">Statement</div>
            <h1 className="mt-1 font-display text-2xl font-bold text-navy-900">AJM Aviation Ventures, LLC</h1>
            <p className="mt-1 text-sm text-ink-muted">101 Main Street, Ste H, Seal Beach, CA 90740</p>
          </div>
          <div className="text-right text-xs text-ink-muted">
            <div>Billing period</div>
            <div className="text-sm font-medium text-navy-900">
              {cycle.start.toLocaleDateString()} — {periodEnd.toLocaleDateString()}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 border-b border-slate-200 py-6 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted">Billed to</div>
            <div className="mt-1 font-medium text-navy-900">{pilotName}</div>
            <div className="text-ink">{profile?.email}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted">Aircraft</div>
            <div className="mt-1 font-medium text-navy-900">
              {aircraft.tailNumber} · {aircraft.year} {aircraft.make} {aircraft.model}
            </div>
            <div className="text-ink">${rateFor(profile, aircraft)}/Hobbs hr · no monthly minimum</div>
          </div>
        </div>

        {cycle.flights.length > 0 && (
          <div className="py-6">
            <h2 className="mb-3 font-display text-base font-semibold text-navy-900">Flights this period</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-ink-muted">
                  <th className="py-2 font-medium">Date</th>
                  <th className="py-2 font-medium">Route</th>
                  <th className="py-2 text-right font-medium">Hobbs hrs</th>
                </tr>
              </thead>
              <tbody>
                {cycle.flights.map((f) => (
                  <tr key={f.flightId} className="border-b border-slate-100">
                    <td className="whitespace-nowrap py-2.5">{f.date}</td>
                    <td className="py-2.5">{f.departureAirport} → {f.destinations.join(' → ')}</td>
                    <td className="py-2.5 text-right">{f.hoursFlown.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="space-y-1 border-t border-navy-900/15 py-5">
          <Row label={`Hobbs hours — ${fmtHours(cycle.hoursFlown)} × ${fmtUSD(cycle.rate)}/hr`} value={fmtUSD(cycle.hoursCharge)} />
          <Row label="Away-from-base" value={fmtUSD(cycle.awayCharge)} />
          <Row label="Total" value={fmtUSD(cycle.totalCharged)} bold />
        </div>

        <div className="mt-6 text-xs text-ink-muted">
          Charges are processed via Stripe to your card on file. Questions?{' '}
          <a href={`mailto:${OWNER_EMAIL}`} className="underline">Reach the owner.</a>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={bold ? 'font-display text-base font-semibold text-navy-900' : 'text-ink'}>{label}</span>
      <span className={bold ? 'font-display text-lg font-bold text-navy-900' : 'font-medium text-ink'}>{value}</span>
    </div>
  );
}
